import { AdminGuard } from '@/components/auth/AdminGuard'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { AppShell } from '@/components/layout/AppShell'
import { useAuthStore } from '@/stores/authStore'
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore'
import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router'

/**
 * /me — "내 페이지". 일반 user 전용 페이지가 아직 없어 role 기반 redirect.
 * - admin → /admin/profile (활동 통계 페이지)
 * - 일반 user → /settings (본인 정보·HF 연동 관리)
 * - 비인증 → /
 *
 * 후속: 일반 user 용 MyPage 추가 시 본 redirect 제거.
 */
function MeRedirect() {
  const user = useAuthStore((s) => s.user)
  if (!user) return <Navigate to="/" replace />
  return <Navigate to={user.role === 'admin' ? '/admin/profile' : '/settings'} replace />
}

const HomePage = lazy(() => import('@/pages/HomePage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const HfCallbackPage = lazy(() => import('@/pages/auth/HfCallbackPage'))
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'))

// Footer 정적 페이지 (공개 — 비로그인 진입 가능)
const AboutPage = lazy(() => import('@/pages/static/AboutPage'))
const ContactPage = lazy(() => import('@/pages/static/ContactPage'))
const DocsPage = lazy(() => import('@/pages/static/DocsPage'))
const LicensePage = lazy(() => import('@/pages/static/LicensePage'))
const PrivacyPage = lazy(() => import('@/pages/static/PrivacyPage'))
const TermsPage = lazy(() => import('@/pages/static/TermsPage'))

// 커뮤니티 진입 허브 (로그인 필수 — 데이터셋/모델 토론 연계)
const CommunityPage = lazy(() => import('@/pages/static/CommunityPage'))

const DatasetCreatePage = lazy(() => import('@/pages/datasets/DatasetCreatePage'))
const DatasetDetailPage = lazy(() => import('@/pages/datasets/DatasetDetailPage'))
const DatasetListPage = lazy(() => import('@/pages/datasets/DatasetListPage'))

const CurationCreatePage = lazy(() => import('@/pages/curations/CurationCreatePage'))
const CurationDetailPage = lazy(() => import('@/pages/curations/CurationDetailPage'))
const CurationListPage = lazy(() => import('@/pages/curations/CurationListPage'))
const CurationSettingsPage = lazy(() => import('@/pages/curations/CurationSettingsPage'))

const ModelCreatePage = lazy(() => import('@/pages/models/ModelCreatePage'))
const ModelDetailPage = lazy(() => import('@/pages/models/ModelDetailPage'))
const ModelListPage = lazy(() => import('@/pages/models/ModelListPage'))

// GDEV-269 / GDEV-274 / GDEV-275 — MLOps 페이지군 (BE 실연동: 학습/평가 K8s Job E2E)
const EvaluationCreatePage = lazy(() => import('@/pages/mlops/EvaluationCreatePage'))
const EvaluationDetailPage = lazy(() => import('@/pages/mlops/EvaluationDetailPage'))
const EvaluationListPage = lazy(() => import('@/pages/mlops/EvaluationListPage'))
const TrainingCreatePage = lazy(() => import('@/pages/mlops/TrainingCreatePage'))
const TrainingDetailPage = lazy(() => import('@/pages/mlops/TrainingDetailPage'))
const TrainingListPage = lazy(() => import('@/pages/mlops/TrainingListPage'))
const TrainingLogsPage = lazy(() => import('@/pages/mlops/TrainingLogsPage'))

// GDEV-268 / GDEV-276 — Leaderboard 페이지군 (BE 실연동: 평가→리더보드 자동 환류)
const LeaderboardDetailPage = lazy(() => import('@/pages/leaderboard/LeaderboardDetailPage'))
const LeaderboardEntryPage = lazy(() => import('@/pages/leaderboard/LeaderboardEntryPage'))
const LeaderboardPage = lazy(() => import('@/pages/leaderboard/LeaderboardPage'))
const LeaderboardSubmitPage = lazy(() => import('@/pages/leaderboard/LeaderboardSubmitPage'))

// Admin
const AdminProfilePage = lazy(() => import('@/pages/admin/AdminProfilePage'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'))
const HfOrgPage = lazy(() => import('@/pages/admin/HfOrgPage'))
const ApprovalsPage = lazy(() => import('@/pages/admin/ApprovalsPage'))
const ModerationPage = lazy(() => import('@/pages/admin/ModerationPage'))
const MonitoringPage = lazy(() => import('@/pages/admin/MonitoringPage'))
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'))

// GDEV-272 — 에러 페이지군
const ForbiddenPage = lazy(() => import('@/pages/errors/ForbiddenPage'))
const MaintenancePage = lazy(() => import('@/pages/errors/MaintenancePage'))
const OfflinePage = lazy(() => import('@/pages/errors/OfflinePage'))
const ServerErrorPage = lazy(() => import('@/pages/errors/ServerErrorPage'))

/**
 * Route tree for the application.
 *
 * Declarative JSX routes are used (vs. the data-router `createBrowserRouter`)
 * to keep the scaffolding simple. When Variant is picked, switch to data
 * routers if route-level loaders/actions become valuable.
 */
export function AppRoutes() {
  const mlopsEnabled = useFeatureFlagsStore((s) => s.mlopsEnabled)

  return (
    <Suspense
      fallback={<div className="p-6 text-sm text-muted-foreground">화면을 불러오는 중...</div>}
    >
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/" element={<HomePage />} />

          {/* Auth [AU-MM-*] */}
          {/* 로그인은 모달, 회원가입은 HF OAuth 콜백이 곧장 홈으로 마무리. */}
          {/* 약관 동의 단계는 AU-MM/GDEV-40 본 연동 시 실 OAuth 프로필 기반으로 신규 작성
            예정 (이전 SignupCompletePage scaffold 는 도달 불가 dead code 라 5/28 제거). */}
          {/* GDEV-177: HF OAuth 콜백 페이지 */}
          <Route path="/auth/hf/callback" element={<HfCallbackPage />} />

          {/* Footer 정적 페이지 — 공개(비로그인 진입 가능). 약관/방침은 발주처
            표준 문구 수령 전 placeholder. */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/license" element={<LicensePage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* ── 로그인 필수 영역 — 비로그인 진입 시 홈 리다이렉트 + 로그인 모달.
            메뉴 5개(Datasets/Curations/Models/MLOps/Leaderboard) + 사용자 설정.
            leaderboard 는 BE 공개지만 FE 정책상 로그인 요구(이중 차단). */}
          <Route element={<RequireAuth />}>
            {/* GDEV-177: 사용자 설정 (HF 연동 관리) */}
            <Route path="/settings" element={<SettingsPage />} />
            {/* /me — UserDropdown "내 페이지" 진입점 (role 기반 redirect) */}
            <Route path="/me" element={<MeRedirect />} />

            {/* 커뮤니티 진입 허브 — 데이터셋/모델 Community 탭 안내 + 바로가기 */}
            <Route path="/community" element={<CommunityPage />} />

            {/* Datasets [ST-DR-*] */}
            <Route path="/datasets" element={<DatasetListPage />} />
            <Route path="/datasets/new" element={<DatasetCreatePage />} />
            <Route path="/datasets/:id" element={<DatasetDetailPage />} />

            {/* Curations [ST-CR-*] */}
            <Route path="/curations" element={<CurationListPage />} />
            <Route path="/curations/new" element={<CurationCreatePage />} />
            <Route path="/curations/:id" element={<CurationDetailPage />} />
            <Route path="/curations/:id/settings" element={<CurationSettingsPage />} />

            {/* Models [ST-MR-*] — 탭 UI 는 ModelDetailPage 내부에서 처리. */}
            <Route path="/models" element={<ModelListPage />} />
            <Route path="/models/new" element={<ModelCreatePage />} />
            <Route path="/models/:id" element={<ModelDetailPage />} />

            {mlopsEnabled && (
              <>
                {/* MLOps [CO-ML-*] — BE 실연동. 학습/평가 K8s Job E2E(UI 제출→GPU Job→완료). */}
                <Route path="/mlops/trainings" element={<TrainingListPage />} />
                <Route path="/mlops/trainings/new" element={<TrainingCreatePage />} />
                <Route path="/mlops/trainings/:id" element={<TrainingDetailPage />} />
                <Route path="/mlops/trainings/:id/logs" element={<TrainingLogsPage />} />
                <Route path="/mlops/evaluations" element={<EvaluationListPage />} />
                <Route path="/mlops/evaluations/new" element={<EvaluationCreatePage />} />
                <Route path="/mlops/evaluations/:id" element={<EvaluationDetailPage />} />
              </>
            )}

            {/* Leaderboard [CO-LB-*] — BE 실연동. 평가 완료→리더보드 자동 환류(verified). */}
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/leaderboard/:slug" element={<LeaderboardDetailPage />} />
            <Route path="/leaderboard/:slug/submit" element={<LeaderboardSubmitPage />} />
            <Route path="/leaderboard/:slug/entries/:entryId" element={<LeaderboardEntryPage />} />
          </Route>
          {/* ── /로그인 필수 영역 ── */}

          {/* Admin [AD-MM-*, AD-AM-04, AD-MO-*] — AdminGuard 로 role=admin 필수 */}
          <Route
            path="/admin/profile"
            element={
              <AdminGuard>
                <AdminProfilePage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminGuard>
                <UsersPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/approvals"
            element={
              <AdminGuard>
                <ApprovalsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/moderation"
            element={
              <AdminGuard>
                <ModerationPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/monitoring"
            element={
              <AdminGuard>
                <MonitoringPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminGuard>
                <AdminSettingsPage />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/hf-org"
            element={
              <AdminGuard>
                <HfOrgPage />
              </AdminGuard>
            }
          />

          {/* GDEV-272 — 에러 페이지 (라우트 가드 / ErrorBoundary 에서 도달) */}
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/offline" element={<OfflinePage />} />

          {/* 404 — catch-all (반드시 마지막) */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
